/**
 * Taxability.ts — SKCATALOG-CC-002 CR-1 (B-D7), console side.
 *
 * SKCATALOG-DB-001 §1: "Taxable items show an HSN/SAC field … Non-taxable
 * (exempt) items don't show the requirement." So the console has to know which
 * items are taxable, and it reads that the same way the API does — from the
 * item's own field values, not from a column.
 *
 * That is deliberate on the backend's part: a boolean about tax status is a
 * domain fact, and domain facts live in the field engine, which is what keeps the
 * catalog free of vertical vocabulary. The consequence here is that taxability is
 * whatever `is_taxable` (or `tax_exempt`, inverted) says on the item being
 * edited, and UNKNOWN when the item's type declares neither — in which case the
 * console shows the field but does not assert that it is REQUIRED, because the
 * server-side default (`CATALOG_TAXABLE_BY_DEFAULT`) decides and the console does
 * not get to guess. If the server refuses the publish anyway, the 422 handler
 * turns it into the same inline block.
 *
 * Mirrors src/services/taxability.ts on the API.
 */

import type { Taxability } from '../schemas/catalog/common';

export const TAXABLE_FIELD_KEY = 'is_taxable';
export const TAX_EXEMPT_FIELD_KEY = 'tax_exempt';

/** JSON booleans arrive as booleans, but a CSV import or a TEXT-typed field can
 *  hold "true"/"1"/"yes". A field that says "yes" has said something. */
function asBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(s)) return true;
    if (['false', '0', 'no', 'n'].includes(s)) return false;
  }
  return null;
}

/**
 * Classify from the editor's flat `{ fieldKey: value }` map.
 *
 * `is_taxable` wins when a blueprint carries both — it is the positive
 * statement, and a type declaring both has said the same thing twice.
 */
export function taxabilityOf(vals: Record<string, unknown>): Taxability {
  const direct = asBool(vals[TAXABLE_FIELD_KEY]);
  if (direct !== null) return direct ? 'TAXABLE' : 'EXEMPT';
  const exempt = asBool(vals[TAX_EXEMPT_FIELD_KEY]);
  if (exempt !== null) return exempt ? 'EXEMPT' : 'TAXABLE';
  return 'UNKNOWN';
}

/** Does the item type carry a taxability field at all? Drives whether the HSN
 *  card explains itself as a GST requirement or just offers the code. */
export function typeDeclaresTaxability(fieldKeys: string[]): boolean {
  return fieldKeys.includes(TAXABLE_FIELD_KEY) || fieldKeys.includes(TAX_EXEMPT_FIELD_KEY);
}

/** Blank, whitespace-only and null all count as "no code set" — a field the
 *  operator tabbed past holds "", and "" is not a code. */
export function hasHsnSac(code: string | null | undefined): boolean {
  return typeof code === 'string' && code.trim().length > 0;
}

/** The exact wording SKCATALOG-DB-001 §1 specifies for the inline block. */
export const HSN_REQUIRED_MESSAGE = 'Set an HSN/SAC code before publishing — required for GST.';
